import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Table, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  EyeOutlined,
  ShoppingOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  getPosHubCatalogCategories,
  getPosHubCatalogModifierGroups,
  getPosHubCatalogModifiers,
  getPosHubCatalogProducts,
} from 'api/poshub';
import { captureException } from 'utils/errors';
import { formatLastModified } from 'utils/date';
import { StyledCatalogTab } from './CatalogTab.styles';

const SIDEBAR_ITEMS = [
  { key: 'categories', label: 'Categories', icon: <AppstoreOutlined /> },
  { key: 'items', label: 'Items', icon: <ShoppingOutlined /> },
  { key: 'modifiers', label: 'Modifiers', icon: <SettingOutlined /> },
  { key: 'modifier_groups', label: 'Modifier Groups', icon: <TeamOutlined /> },
];

const CATALOG_EMPTY_TEXT = {
  categories: 'No categories returned for this location. If the POS is connected, try syncing catalog from the Apps tab.',
  items: 'No items returned for this location. If the POS is connected, try syncing catalog from the Apps tab.',
  modifiers: 'No modifiers returned for this location. If the POS is connected, try syncing catalog from the Apps tab.',
  modifier_groups:
    'No modifier groups returned for this location. If the POS is connected, try syncing catalog from the Apps tab.',
};

function CatalogTab({ restaurantId }) {
  const [activeSection, setActiveSection] = useState('categories');
  const [data, setData] = useState({ data: [], hasNextPage: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    const fetchers = {
      categories: () => getPosHubCatalogCategories(restaurantId, { limit: 50 }),
      items: () => getPosHubCatalogProducts(restaurantId, { limit: 50 }),
      modifiers: () => getPosHubCatalogModifiers(restaurantId, { limit: 50 }),
      modifier_groups: () => getPosHubCatalogModifierGroups(restaurantId, { limit: 50 }),
    };
    const fn = fetchers[activeSection];
    if (!fn) {
      setData({ data: [], hasNextPage: false });
      setLoading(false);
      return;
    }
    fn()
      .then((res) => setData({ data: res.data || [], hasNextPage: res.hasNextPage || false }))
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.detail || 'Could not load catalog. Make sure this restaurant is connected to the POS integration.');
        setData({ data: [], hasNextPage: false });
      })
      .finally(() => setLoading(false));
  }, [restaurantId, activeSection]);

  const catalogEmptyText =
    CATALOG_EMPTY_TEXT[activeSection] ?? 'No data for this catalog section.';

  /**
   * Percent widths sum to 100% so columns stay grouped (no huge dead space between Name and the rest).
   */
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: activeSection === 'items' ? '34%' : '42%',
    },
    {
      title: 'Show online',
      dataIndex: 'showOnline',
      key: 'showOnline',
      width: activeSection === 'items' ? '11%' : '13%',
      render: (val) => (val != null ? (val ? 'Yes' : 'No') : '-'),
    },
    ...(activeSection === 'items'
      ? [{
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        width: '11%',
        render: (v, row) => {
          const raw = v ?? row?.priceCents ?? row?.price?.amount ?? row?.price?.value ?? null;
          if (raw == null) return '-';
          const dollars = Number(raw) / 100;
          return `$${dollars.toFixed(2)}`;
        },
      }]
      : []),
    {
      title: 'Last modified',
      key: 'last_modified',
      width: activeSection === 'items' ? '36%' : '37%',
      render: (_, row) => formatLastModified(row.updatedAt),
    },
    {
      title: '',
      key: 'actions',
      width: '8%',
      align: 'center',
      render: (_, row) => (
        <Tooltip title="View details">
          <Button
            type="text"
            size="small"
            className="poshub-row-action-btn"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRow(row);
              setDrawerOpen(true);
            }}
            aria-label="View details"
          />
        </Tooltip>
      ),
    },
  ];

  const renderDetailValue = (val) => {
    if (val == null || val === '') return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const renderDetailsContent = () => {
    if (!selectedRow) return null;
    const row = selectedRow;
    const isItem = activeSection === 'items';
    const isModifier = activeSection === 'modifiers';
    const isModifierGroup = activeSection === 'modifier_groups';
    const priceRaw = row.price ?? row.priceCents ?? row.price?.amount ?? row.price?.value;
    const priceDisplay = priceRaw != null ? `$${(Number(priceRaw) / 100).toFixed(2)}` : '—';
    const modifierNames = Array.isArray(row.modifiers)
      ? row.modifiers.map((m) => {
        if (m == null) return null;
        if (typeof m === 'string') return m;
        if (typeof m === 'object') {
          if (m.name != null && m.name !== '') return String(m.name);
          if (m.id != null) return String(m.id);
        }
        return null;
      }).filter(Boolean)
      : [];
    const items = [
      { label: 'Name', value: row.name },
      { label: 'ID', value: row.id },
      { label: 'Show online', value: renderDetailValue(row.showOnline) },
      { label: 'Position', value: renderDetailValue(row.position) },
      { label: 'Description', value: renderDetailValue(row.description) },
      { label: 'Pos reference', value: renderDetailValue(row.posReference) },
      ...(isModifierGroup
        ? [
          { label: 'Min selections', value: renderDetailValue(row.minPermitted) },
          { label: 'Max selections', value: renderDetailValue(row.maxPermitted) },
          {
            label: 'Modifiers in group',
            value: modifierNames.length ? modifierNames.join(', ') : '—',
          },
        ]
        : []),
      ...(isItem || isModifier ? [{ label: 'Price', value: priceDisplay }] : []),
      ...(isItem ? [{ label: 'Type', value: renderDetailValue(row.type) }] : []),
      { label: 'Created', value: row.createdAt ? formatLastModified(row.createdAt) : '—' },
      { label: 'Updated', value: row.updatedAt ? formatLastModified(row.updatedAt) : '—' },
    ];
    if (isItem && (row.originalImageUrl || row.imageUrl)) {
      items.push({
        label: 'Image',
        value: (
          <a href={row.originalImageUrl || row.imageUrl} target="_blank" rel="noopener noreferrer">
            Open image
          </a>
        ),
      });
    }
    return (
      <Descriptions column={1} size="middle" bordered className="poshub-catalog-descriptions">
        {items.map(({ label, value }, idx) => (
          <Descriptions.Item key={`catalog-detail-${idx}`} label={label}>
            {value}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  };

  return (
    <StyledCatalogTab>
      <div className="catalog-layout">
        <nav className="catalog-sidebar">
          {SIDEBAR_ITEMS.map(({ key, label, icon }) => (
            <button
              key={key}
              type="button"
              className={`catalog-nav-item ${activeSection === key ? 'active' : ''}`}
              onClick={() => setActiveSection(key)}
            >
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="catalog-main">
          <div className="catalog-section-title">
            {SIDEBAR_ITEMS.find((i) => i.key === activeSection)?.label ?? 'Catalog'}
          </div>
          {error && (
            <Alert
              type="warning"
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            className="poshub-console-table"
            tableLayout="fixed"
            columns={columns}
            dataSource={data.data}
            rowKey="id"
            loading={loading}
            pagination={false}
            locale={{
              emptyText: error ? 'Unable to load this list. See the message above.' : catalogEmptyText,
            }}
          />
        </div>
      </div>

      <Drawer
        className="poshub-catalog-drawer"
        title={selectedRow?.name ? `Details: ${selectedRow.name}` : 'Details'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRow(null);
        }}
        width={520}
        destroyOnClose
      >
        {renderDetailsContent()}
      </Drawer>
    </StyledCatalogTab>
  );
}

export default CatalogTab;
